import { useCallback } from 'react'
import _ from 'lodash'
import { ColumnTable } from 'arquero'
import { PinnedMorphotype } from 'redux/slices'
import { DifferentialFeaturesComparison, MergedMorphotype } from 'redux/slices/types'
import { getMetadataFromDataFrame } from './plot/utils'
import reconstructMorphotypeCoordinates, { reconstructMergedMorphotypeCoordinates } from './morphotypeDataOptimization'
import { migrateMorphotypes, VersionConfig } from './migrateMorphotypes'
import useRecomputeDifferentialFeatures from './useRecomputeDifferentialFeatures'

export type MigrationProgressCallback = (step: 'morphotypes' | 'differential-features', count?: number) => void

interface MorphotypeMigrationInput {
  pinnedCells: PinnedMorphotype[] | undefined
  morphotypesToRemove: PinnedMorphotype[] | undefined
  mergedPinnedCells: MergedMorphotype[] | undefined
  morphotypeFormatVersion: number | undefined
  differentialFeaturesComparisons: DifferentialFeaturesComparison[] | undefined
  dataFrame: ColumnTable
  prefilteredCellIdSet: Set<string>
  sessionId: number
  versionId: number
  onProgress?: MigrationProgressCallback
}

interface MorphotypeMigrationResult {
  migratedPinnedCells: PinnedMorphotype[] | undefined
  reconstructedMorphotypesToRemove: PinnedMorphotype[] | undefined
  reconstructedMergedPinnedCells: MergedMorphotype[] | undefined
  updatedMorphotypeFormatVersion: number | undefined
  recomputedDifferentialFeaturesComparisons: DifferentialFeaturesComparison[] | undefined
}

/**
 * Custom hook that handles morphotype migration and differential features recomputation.
 * Uses DataFrame for memory efficiency.
 */
export default function useMorphotypeMigration(): {
  migrateIfNeeded: (input: MorphotypeMigrationInput) => Promise<MorphotypeMigrationResult>
} {
  const {
    recomputeForMigratedMorphotypes,
  } = useRecomputeDifferentialFeatures()

  /**
   * Migrates morphotypes from old format to new format if needed.
   * Also reconstructs coordinate data and recomputes differential features for migrated morphotypes.
   * Uses DataFrame for memory efficiency.
   */
  const migrateIfNeeded = useCallback(
    async (input: MorphotypeMigrationInput): Promise<MorphotypeMigrationResult> => {
      const {
        pinnedCells,
        morphotypesToRemove,
        mergedPinnedCells,
        morphotypeFormatVersion,
        differentialFeaturesComparisons,
        dataFrame,
        prefilteredCellIdSet,
        sessionId,
        versionId,
        onProgress,
      } = input

      // Reconstruct coordinate data for optimized morphotypes using DataFrame
      const reconstructedPinnedCells = pinnedCells
        ? reconstructMorphotypeCoordinates(pinnedCells, dataFrame)
        : undefined

      const reconstructedMorphotypesToRemove = morphotypesToRemove
        ? reconstructMorphotypeCoordinates(morphotypesToRemove, dataFrame)
        : undefined

      // Reconstruct mergedPinnedCells coordinates from compressed format (if needed)
      // This is critical: mergedPinnedCells may be in OptimizedMergedMorphotype format
      // (with only cellIds) and need to be reconstructed to have full coordinate data
      const reconstructedMergedPinnedCells = mergedPinnedCells
        ? reconstructMergedMorphotypeCoordinates(mergedPinnedCells, dataFrame)
        : undefined

      // Setup version config for migration check
      const versionConfig: VersionConfig = {
        morphotypeFormatVersion,
      }

      let migratedPinnedCells = reconstructedPinnedCells
      let updatedMorphotypeFormatVersion = morphotypeFormatVersion
      let recomputedDifferentialFeaturesComparisons = differentialFeaturesComparisons
      let updatedMergedPinnedCells = reconstructedMergedPinnedCells

      // Only run migration if not already version 2
      if (!versionConfig.morphotypeFormatVersion || versionConfig.morphotypeFormatVersion < 2) {
        // Compute scatterPlotMetaData using DataFrame
        const scatterPlotMetaData = getMetadataFromDataFrame(dataFrame)

        const {
          morphotypes: migratedMorphotypes,
          versionConfig: updatedVersionConfig,
          migratedMorphotypeIds,
        } = migrateMorphotypes(
          reconstructedPinnedCells || [],
          versionConfig,
          dataFrame,
          prefilteredCellIdSet,
          scatterPlotMetaData
        )

        migratedPinnedCells = migratedMorphotypes
        updatedMorphotypeFormatVersion = updatedVersionConfig.morphotypeFormatVersion

        // Update mergedPinnedCells to use migrated cell data
        if (updatedMergedPinnedCells && updatedMergedPinnedCells.length > 0) {
          updatedMergedPinnedCells = updatedMergedPinnedCells.map((mergedMorphotype) => {
            const migratedVersions = migratedMorphotypes.filter((m) => m.id === mergedMorphotype.id)
            if (migratedVersions.length > 0) {
              const allPoints = migratedVersions.flatMap((m) => m.cells.points ?? [])
              const uniquePoints = _.uniqBy(
                allPoints,
                (p) => p.cellId || `${p.originalX ?? p.x}_${p.originalY ?? p.y}`
              )
              return {
                ...mergedMorphotype,
                cells: { points: uniquePoints },
              }
            }
            return mergedMorphotype
          })
        }

        // Notify progress after morphotype migration
        if (migratedMorphotypeIds.length > 0 && onProgress) {
          onProgress('morphotypes', migratedMorphotypeIds.length)
        }

        // Only recompute differential features if morphotypes were actually migrated
        if (migratedMorphotypeIds.length > 0) {
          recomputedDifferentialFeaturesComparisons = await recomputeForMigratedMorphotypes(
            differentialFeaturesComparisons,
            migratedMorphotypeIds,
            sessionId,
            versionId,
            dataFrame,
            migratedMorphotypes
          )

          if (onProgress) {
            onProgress('differential-features', migratedMorphotypeIds.length)
          }
        }
      }

      return {
        migratedPinnedCells,
        reconstructedMorphotypesToRemove,
        reconstructedMergedPinnedCells: updatedMergedPinnedCells,
        updatedMorphotypeFormatVersion,
        recomputedDifferentialFeaturesComparisons,
      }
    },
    [recomputeForMigratedMorphotypes]
  )

  return { migrateIfNeeded }
}
